import React, { useEffect, useState } from 'react';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError, useAuth } from '@/App';
import { User, Buildings, Envelope, FloppyDisk } from '@phosphor-icons/react';

const PortalProfile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proxy, setProxy] = useState(null);
  const [company, setCompany] = useState(null);

  const fetchProfile = async () => {
    try {
      const { data } = await api.get('/portal/profile');
      setProxy(data.proxy || {});
      setCompany(data.company || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const proxyPayload = {
        first_name: proxy.first_name,
        surname: proxy.surname,
        id_number: proxy.id_number || null,
        personal_phone: proxy.personal_phone || null,
      };
      const companyPayload = company
        ? {
            company_name: company.company_name,
            company_phone: company.company_phone || null,
            physical_address: company.physical_address || null,
            vat_number: company.vat_number || null,
            residential_address: company.residential_address || null,
            id_number: company.id_number || null,
          }
        : null;
      const { data } = await api.put('/portal/profile', { proxy: proxyPayload, company: companyPayload });
      setProxy(data.proxy);
      setCompany(data.company);
      toast.success('Profile updated');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PortalLayout>
    );
  }

  const isBusiness = !company || company.account_type !== 'personal';

  return (
    <PortalLayout title="My Profile">
      <div className="max-w-4xl mx-auto space-y-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F]">My Profile</h1>
          <p className="text-sm text-gray-500 mt-1">Keep your details up to date so we can serve you better.</p>
        </div>

        {/* Personal Details */}
        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#4CAF50]" /> Personal Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">First Name *</Label>
                <Input
                  value={proxy?.first_name || ''}
                  onChange={(e) => setProxy({ ...proxy, first_name: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="profile-first-name"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Surname *</Label>
                <Input
                  value={proxy?.surname || ''}
                  onChange={(e) => setProxy({ ...proxy, surname: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="profile-surname"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold flex items-center gap-1">
                  <Envelope className="w-4 h-4" /> Email (login)
                </Label>
                <Input
                  value={proxy?.personal_email || user?.email || ''}
                  disabled
                  className="rounded-none mt-1 bg-gray-50 text-gray-500"
                  data-testid="profile-email"
                />
                <p className="text-xs text-gray-400 mt-1">Contact support to change your login email.</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Phone</Label>
                <Input
                  value={proxy?.personal_phone || ''}
                  onChange={(e) => setProxy({ ...proxy, personal_phone: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="profile-phone"
                  placeholder="+27 ..."
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">ID Number (POPIA)</Label>
              <Input
                value={proxy?.id_number || ''}
                onChange={(e) => setProxy({ ...proxy, id_number: e.target.value })}
                className="rounded-none mt-1 font-mono"
                placeholder="13-digit SA ID"
                data-testid="profile-id-number"
              />
            </div>
          </CardContent>
        </Card>

        {/* Company / Account Details */}
        {company && (
          <Card>
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Buildings className="w-5 h-5 text-[#4CAF50]" />
                {isBusiness ? 'Company Details' : 'Account Details'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-sm font-semibold">
                  {isBusiness ? 'Company Name *' : 'Full Account Name *'}
                </Label>
                <Input
                  value={company.company_name || ''}
                  onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="profile-company-name"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Account Email</Label>
                  <Input
                    value={company.company_email || ''}
                    disabled
                    className="rounded-none mt-1 bg-gray-50 text-gray-500"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">{isBusiness ? 'Company Phone' : 'Phone'}</Label>
                  <Input
                    value={company.company_phone || ''}
                    onChange={(e) => setCompany({ ...company, company_phone: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid="profile-company-phone"
                  />
                </div>
              </div>

              {isBusiness ? (
                <>
                  <div>
                    <Label className="text-sm font-semibold">Physical Address</Label>
                    <Input
                      value={company.physical_address || ''}
                      onChange={(e) => setCompany({ ...company, physical_address: e.target.value })}
                      className="rounded-none mt-1"
                      data-testid="profile-physical-address"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">VAT Number</Label>
                    <Input
                      value={company.vat_number || ''}
                      onChange={(e) => setCompany({ ...company, vat_number: e.target.value })}
                      className="rounded-none mt-1 font-mono"
                      data-testid="profile-vat-number"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-sm font-semibold">Residential Address</Label>
                    <Input
                      value={company.residential_address || ''}
                      onChange={(e) => setCompany({ ...company, residential_address: e.target.value })}
                      className="rounded-none mt-1"
                      data-testid="profile-residential-address"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">ID Number</Label>
                    <Input
                      value={company.id_number || ''}
                      onChange={(e) => setCompany({ ...company, id_number: e.target.value })}
                      className="rounded-none mt-1 font-mono"
                      placeholder="13-digit SA ID"
                      data-testid="profile-account-id-number"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none"
            data-testid="profile-save-btn"
          >
            <FloppyDisk className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalProfile;
